import { runInAction } from 'mobx';

import { Player } from '../model/Player';
import { WaitingHostBot } from '../model/WaitingHostBot';
import { WPlayer } from '../model/websocket/WPlayer';
import { GameTable } from './state/GameTable';

// util class for players. Will not make Players as mobx observable because can be bad performance (too many observables table count * player count)
class PlayerStore {
  public modifyPlayers = (
    players: (Player | null)[],
    pidToModify: string,
    action: (player: Player) => void,
  ): (Player | null)[] => {
    return players.map(player => {
      if (player === null) {
        return null;
      }
      if (player.pid === pidToModify) {
        runInAction(() => action(player));
      }
      return player;
    });
  };

  public modifyAllPlayersWithSingleAction = (
    players: (Player | null)[],
    action: (player: Player) => void,
  ): (Player | null)[] => {
    return players.map(player => {
      if (player === null) {
        return null;
      }
      runInAction(() => action(player));
      return player;
    });
  };

  public modifyPlayerList = (
    source: (WPlayer | null)[],
    target: (Player | null)[],
    action: (source: WPlayer, target: Player) => void,
  ): (Player | null)[] => {
    return target.map(player => {
      if (player === null) {
        return null;
      }
      const sourcePlayer = source.find(s => s?.pid === player.pid);
      if (sourcePlayer) {
        runInAction(() => action(sourcePlayer, player));
      }
      return player;
    });
  };

  public findBots = (players: Player[]): Player[] =>
    players.filter(player => player?.myBot) || [];

  public findPlayers = (table: GameTable): Player[] =>
    table.players.filter(player => player);

  public findPlayer = (pid: string, players: Player[]): Player | undefined =>
    players.find(player => player?.pid === pid);

  public getTotalPlayingBotsCount = (tables: GameTable[]): number =>
    new Set(
      tables
        .flatMap(table => table.players)
        .filter(player => player?.myBot)
        .map(player => player.pid),
    ).size;

  public findLivePlayersCount = (table: GameTable): number =>
    table.players.filter(player => player && !player.myBot).length;

  public findBotsCount = (table: GameTable): number =>
    this.findBots(table.players).length;

  public findWaitingHostRebuyBots = (table: GameTable): WaitingHostBot[] =>
    table.players
      .filter(player => player && player.myBot && player.waitingRebuy)
      .map(player => {
        return {
          pid: player.pid,
          nick: player.nick,
          club: player.club,
          uniqueId: player.uniqueId,
          amount: player.waitingRebuyAmount,
        };
      });
}

const playerStore = new PlayerStore();
export default playerStore;
